<script>
import FileDownload from 'downloadjs';
import { groupBy, orderBy, trim } from 'lodash';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import { FilterComponent, GroupBy, SortComponent, TableList, VisibleProperty } from '../components';
import { columns } from '../config';

export default {
	name: "dailyCloseoutReport",
	components: {
		SortComponent,
		VisibleProperty,
		GroupBy,
		FilterComponent,
		TableList
	},
	data() {
		return {
			branch: [],
			subprograms: [],
			feeTypes: [],
			statuses: [
				{ value: 'Paid', label: 'Paid' },
				{ value: 'Unpaid', label: 'Unpaid' },
				{ value: 'Void', label: 'Void' },
			],
			visible: {
				cols: []
			},
			show_pdf: false,
			pdf_generate: false,
			excel_generate: false,
			src: '',
			model: {
				branch_id: undefined,
				acps_id: undefined,
				fee_type_name: undefined,
				status: undefined,
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				sort: [{ col: "payment_date", text: "Invoice Date", order: "asc" }, { col: "invoice_number", text: "Receipt #", order: "asc" }],
				groupBy: undefined,
				visible: [],
				filters: [
				]
			},
			loading: false
		}
	},
	computed: {
		...mapState('dailyCloseoutReport', ['resources', 'pagination', 'summary']),
		prepareResource() {
			let tmpResource = this.resources;
			if (this.model.groupBy) {
				return groupBy(tmpResource, this.model.groupBy)
			}

			return tmpResource;
		},
		columnsTable() {
			return this.visible.cols
				.map(el => ({
					key: el.key || el.col,
					label: this.$t(`dailyCloseout.${el.label}`),
					className: el.className,
					slot: el.slot,
					numberFormat: el.numberFormat
				}));
		}
	},
	created() {
		const filterOption = localStorage.getItem('daily_closeout_report_filter')
		if (filterOption) {
			this.model.filters = JSON.parse(filterOption)
		}

		const visibleColOrder = localStorage.getItem('daily_closeout_report_visible_order')
		if (visibleColOrder) {
			this.visible.cols = JSON.parse(visibleColOrder)
		} else {
			this.visible.cols = columns()
		}
		const visibleCol = localStorage.getItem('daily_closeout_report_visible')
		if (visibleCol) {
			this.model.visible = JSON.parse(visibleCol)
		} else {
			this.model.visible = this.columnsTable.map(el => el.key)
		}
	},
	mounted() {
		this.fetchResource()
	},
	methods: {
		...mapActions('dailyCloseoutReport', ['fetch', 'pdf', 'getBranch', 'toExcel', 'getSubProgram', 'getFeeTypes']),
		onSearch(attribute = {}) {
			this.loading = true
			this.fetch(Object.assign({}, this.model, attribute))
				.finally(() => this.loading = false)

		},
		onPdf(attribute = {}) {
			this.show_pdf = true
			this.pdf_generate = true
			this.src = ''
			this.pdf(Object.assign({}, this.model, attribute))
				.then(response => {
					this.src = response.data.url
				})
				.finally(() => this.pdf_generate = false)
		},
		onExcel(attribute = {}) {
			this.excel_generate = true
			this.toExcel(Object.assign({}, this.model, attribute))
				.then(response => {
					let filename = trim(
						response.headers['content-disposition']
							.substring(
								response.headers['content-disposition'].indexOf('filename')
							)
							.replace('filename=', ''),
						'"'
					)
					FileDownload(response.data, filename)
				})
				.catch(err => {
					let error = JSON.parse(
						String.fromCharCode.apply(null, new Uint8Array(err.response.data))
					)
					this.$toasted.error(error.message)
				})
				.finally(() => this.excel_generate = false)
		},
		async fetchResource() {
			this.loading = true

			const [branchResponse, subProgramResponse, feeTypesResponse] = await Promise.all([
				this.getBranch(),
				this.getSubProgram(),
				this.getFeeTypes()
			]);

			this.branch = branchResponse.data.map(el => ({
				value: el.branch_id,
				label: el.branch_name_en
			}))

			this.subprograms = subProgramResponse.data.map(el => ({
				value: el.acps_id,
				label: el.acps_name_foreign
			}))

			this.feeTypes = feeTypesResponse.data.map(name => ({
				value: name,
				label: name
			}))

			this.loading = false
		},
		formatDisplay(date) {
			return date ? date.format('DD-MMM-YYYY') : '';
		},
		filterOption(input, option) {
			return option.componentOptions.children[0].text
				.toLowerCase()
				.indexOf(input.toLowerCase()) >= 0
		},
	}

}
</script>

<template>
	<div>
		<div class="d-flex justify-content-between ts-mb-3">
			<div>
				<h1 class="page-header ts-mb-0">
					{{ $t('dailyCloseout.pageTitle') }}
				</h1>
			</div>
			<ts-breadcrumb :items="[
				{ text: $t('toHomePage'), href: '/' },
				{
					text: $t('dailyCloseout.pageTitle'),
					active: true
				}
			]" />
		</div>

		<div class="ts-space-y-3">
			<ts-card>
				<ts-card-wrapper>
					<div class="ts-space-y-2">
						<div class="ts-flex ts-space-x-2 ts-border-b ts-pb-2">
							<SortComponent v-model="model" @fetch="onSearch" />
							<GroupBy v-model="model" />
							<VisibleProperty v-model="model" :cols="visible" />
							<FilterComponent v-model="model" @search="onSearch" />
						</div>
						<div>
							<div class="ts-flex ts-space-x-2 ts-items-center">
								<a-select v-model="model.branch_id" :options="branch" :allowClear="true" style="width: 200px;"
									:placeholder="$t('dailyCloseout.selectBranch')"></a-select>
								<a-select v-model="model.acps_id" :options="subprograms" :allowClear="true" mode="multiple" :maxTagCount="1"
									 :maxTagTextLength="20" style="width: 280px;"
									:placeholder="$t('dailyCloseout.selectSubProgram')" :showSearch="true"
									:filterOption="filterOption"></a-select>
								<a-select v-model="model.fee_type_name" :options="feeTypes" :allowClear="true" style="width: 180px;"
									:placeholder="$t('dailyCloseout.feeTypeName')" :showSearch="true"
									:filterOption="filterOption"></a-select>
								<a-select v-model="model.status" :options="statuses" :allowClear="true" style="width: 130px;"
									:placeholder="$t('dailyCloseout.status')"></a-select>
								<a-date-picker v-model="model.dateFrom" :format="formatDisplay" valueFormat="YYYY-MM-DD"
									:allowClear="false" :placeholder="$t('dailyCloseout.dateFrom')" class="ts-w-32" />
								<b><i class="fas fa-arrow-right"></i></b>
								<a-date-picker v-model="model.dateTo" :format="formatDisplay" valueFormat="YYYY-MM-DD"
									:allowClear="false" :placeholder="$t('dailyCloseout.dateTo')" class="ts-w-32" />
								<button class="ts-bg-blue-500 ts-text-white ts-px-3 ts-py-2 ts-rounded focus:ts-outline-none"
									@click.prevent="onSearch">
									<a-icon type="search" />
									{{ $t('dailyCloseout.search') }}
								</button>
								<button @click.prevent="onPdf"
									class="ts-border ts-border-red-500 ts-text-red-500 ts-px-6 ts-rounded ts-cursor-pointer focus:ts-outline-none"
									style="padding: 5px 20px;">
									<i class="fas fa-file-pdf" style="font-size: 16px;"></i>
								</button>
								<button @click.prevent="onExcel" :disabled="excel_generate"
									class="ts-border ts-border-green-500 ts-text-green-500 ts-px-6 ts-rounded ts-cursor-pointer focus:ts-outline-none"
									style="padding: 5px 20px;">
									<i class="far fa-file-excel" style="font-size: 16px;" v-show="!excel_generate"></i>
									<i class="fas fa-spinner fa-spin" v-show="excel_generate"></i>
								</button>
							</div>
						</div>
					</div>
				</ts-card-wrapper>
			</ts-card>

			<!-- Summary Cards -->
			<div class="ts-grid ts-grid-cols-4 ts-gap-2">
				<div class="ts-rounded ts-border ts-px-3 ts-py-2" style="background:#f0faf4; border-color:#b7ebc8;">
					<div class="ts-flex ts-items-center ts-space-x-1 ts-mb-0.5">
						<span class="ts-text-green-600" style="font-size:11px;"><i class="fas fa-dollar-sign"></i></span>
						<span class="ts-font-semibold ts-text-green-700 ts-uppercase" style="font-size:10px;">{{ $t('dailyCloseout.totalAmount') }}</span>
					</div>
					<div class="ts-font-bold ts-text-green-700" style="font-size:13px;">
						$ {{ summary.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
					</div>
				</div>
				<div class="ts-rounded ts-border ts-px-3 ts-py-2" style="background:#fffbf0; border-color:#fde68a;">
					<div class="ts-flex ts-items-center ts-space-x-1 ts-mb-0.5">
						<span class="ts-text-yellow-600" style="font-size:11px;"><i class="fas fa-tag"></i></span>
						<span class="ts-font-semibold ts-text-yellow-700 ts-uppercase" style="font-size:10px;">{{ $t('dailyCloseout.totalDiscount') }}</span>
					</div>
					<div class="ts-font-bold ts-text-yellow-700" style="font-size:13px;">
						$ {{ summary.total_discount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
					</div>
				</div>
				<div class="ts-rounded ts-border ts-px-3 ts-py-2" style="background:#eff6ff; border-color:#bfdbfe;">
					<div class="ts-flex ts-items-center ts-space-x-1 ts-mb-0.5">
						<span class="ts-text-blue-600" style="font-size:11px;"><i class="fas fa-table"></i></span>
						<span class="ts-font-semibold ts-text-blue-700 ts-uppercase" style="font-size:10px;">{{ $t('dailyCloseout.grandTotal') }}</span>
					</div>
					<div class="ts-font-bold ts-text-blue-700" style="font-size:13px;">
						$ {{ summary.grand_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
					</div>
				</div>
				<div class="ts-rounded ts-border ts-px-3 ts-py-2" style="background:#fff5f5; border-color:#fecaca;">
					<div class="ts-flex ts-items-center ts-space-x-1 ts-mb-0.5">
						<span class="ts-text-red-600" style="font-size:11px;"><i class="fas fa-trash"></i></span>
						<span class="ts-font-semibold ts-text-red-700 ts-uppercase" style="font-size:10px;">{{ $t('dailyCloseout.totalDeleteInvoice') }}</span>
					</div>
					<div class="ts-font-bold ts-text-red-700" style="font-size:13px;">
						$ {{ summary.deleted_invoices.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
					</div>
					<div class="ts-text-red-500" style="font-size:10px;">{{ summary.deleted_invoices.count }} {{ $t('dailyCloseout.invoices') }}</div>
				</div>
			</div>

			<!-- Summary Tables -->
			<div class="ts-grid ts-grid-cols-2 ts-gap-2">
				<ts-card>
					<ts-card-wrapper style="padding: 10px 14px;">
						<div class="ts-flex ts-items-center ts-space-x-2 ts-mb-2">
							<i class="fas fa-credit-card ts-text-gray-400" style="font-size:12px;"></i>
							<span class="ts-font-semibold ts-text-gray-600 ts-uppercase" style="font-size:11px;">{{ $t('dailyCloseout.totalByPaymentMethod') }}</span>
						</div>
						<table class="ts-w-full" style="font-size:12px;">
							<thead>
								<tr class="ts-bg-gray-100">
									<th class="ts-text-left ts-px-2" style="padding-top:4px;padding-bottom:4px;">#</th>
									<th class="ts-text-left ts-px-2" style="padding-top:4px;padding-bottom:4px;">{{ $t('dailyCloseout.paymentMethod') }}</th>
									<th class="ts-text-right ts-px-2" style="padding-top:4px;padding-bottom:4px;">Total (USD)</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(item, index) in summary.method_summary.details" :key="index" class="ts-border-b">
									<td class="ts-px-2 ts-text-gray-400" style="padding-top:4px;padding-bottom:4px;">{{ index + 1 }}</td>
									<td class="ts-px-2" style="padding-top:4px;padding-bottom:4px;">{{ item.method }}</td>
									<td class="ts-px-2 ts-text-right" style="padding-top:4px;padding-bottom:4px;">$ {{ Number(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
								</tr>
							</tbody>
							<tfoot>
								<tr class="ts-font-bold ts-border-t-2">
									<td colspan="2" class="ts-px-2" style="padding-top:4px;padding-bottom:4px;">{{ $t('dailyCloseout.grandTotal') }}</td>
									<td class="ts-px-2 ts-text-right" style="padding-top:4px;padding-bottom:4px;">$ {{ Number(summary.method_summary.sum_in_dollar).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
								</tr>
							</tfoot>
						</table>
					</ts-card-wrapper>
				</ts-card>

				<ts-card>
					<ts-card-wrapper style="padding: 10px 14px;">
						<div class="ts-flex ts-items-center ts-space-x-2 ts-mb-2">
							<i class="fas fa-credit-card ts-text-gray-400" style="font-size:12px;"></i>
							<span class="ts-font-semibold ts-text-gray-600 ts-uppercase" style="font-size:11px;">{{ $t('dailyCloseout.totalByFeeTypes') }}</span>
						</div>
						<table class="ts-w-full" style="font-size:12px;">
							<thead>
								<tr class="ts-bg-gray-100">
									<th class="ts-text-left ts-px-2" style="padding-top:4px;padding-bottom:4px;">#</th>
									<th class="ts-text-left ts-px-2" style="padding-top:4px;padding-bottom:4px;">{{ $t('dailyCloseout.feeType') }}</th>
									<th class="ts-text-right ts-px-2" style="padding-top:4px;padding-bottom:4px;">Total (USD)</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(item, index) in summary.fee_type_summary" :key="index" class="ts-border-b">
									<td class="ts-px-2 ts-text-gray-400" style="padding-top:4px;padding-bottom:4px;">{{ index + 1 }}</td>
									<td class="ts-px-2" style="padding-top:4px;padding-bottom:4px;">{{ item.fee_type_name }}</td>
									<td class="ts-px-2 ts-text-right" style="padding-top:4px;padding-bottom:4px;">$ {{ Number(item.total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
								</tr>
							</tbody>
							<tfoot>
								<tr class="ts-font-bold ts-border-t-2">
									<td colspan="2" class="ts-px-2" style="padding-top:4px;padding-bottom:4px;">{{ $t('dailyCloseout.grandTotal') }}</td>
									<td class="ts-px-2 ts-text-right" style="padding-top:4px;padding-bottom:4px;">$ {{ summary.grand_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
								</tr>
							</tfoot>
						</table>
					</ts-card-wrapper>
				</ts-card>
			</div>

			<ts-card>
				<div>
					<ts-loading-banner :loading="loading">
						<TableList :columns="columnsTable" :visible="model.visible" :data="prepareResource">
							<template #discount="{ row }">
								{{ row.discount_rate }} {{ row.discount_type }}
							</template>
						</TableList>
					</ts-loading-banner>
				</div>
				<div class="d-flex justify-content-between align-items-center p-15">
					<ts-total-list v-model="pagination" />
					<ts-pagination v-model="pagination" @navigate="onSearch"></ts-pagination>
				</div>
			</ts-card>
		</div>

		<a-modal :title="$t('pdf')" width="70%" v-model="show_pdf" :footer="null">
			<ts-preview-pdf v-model="pdf_generate" :src="src" />
		</a-modal>
	</div>
</template>